import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../../auth/AuthProvider';
import { permissions } from '../../../auth/permissions';
import { useMachines } from '../../machines/api/useMachines';
import { useDocuments } from '../api/useDocuments';
import { DocumentStatusBadge } from '../components/DocumentStatusBadge';
import { IncrementalListFooter } from '../../../components/ui/IncrementalListFooter';
import { useIncrementalItems } from '../../../components/ui/useIncrementalItems';
import { STANDARD_LIST_BATCH_SIZE } from '../../../components/ui/list-batch-sizes';
import { documentReconciliationCompletedGuidance } from '../removal-guidance';
import { formatFileSize, type DocumentUiStatus } from '../types/document';

const activeFilters: Array<{ value: 'all' | DocumentUiStatus; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'learning', label: 'Learning' },
  { value: 'upload-interrupted', label: 'Upload interrupted' },
  { value: 'ready', label: 'Ready' },
  { value: 'no-evidence', label: 'No clear evidence' },
  { value: 'needs-review', label: 'Needs review' },
  { value: 'failed', label: 'Failed' },
];

export function DocumentsPage() {
  const { machineId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const machines = useMachines();
  const removedView = searchParams.get('view') === 'removed';
  const documents = useDocuments(
    machineId,
    auth.can(permissions.documentsRead),
    removedView ? 'REMOVED' : 'ACTIVE',
  );
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | DocumentUiStatus>('all');
  const [reconciliationCompleted, setReconciliationCompleted] = useState(false);
  const previousReconcilingCount = useRef<number | null>(null);
  const machine = machines.data?.machines.find((item) => item.id === machineId);
  const firstMachineOnboarding = !removedView && searchParams.get('onboarding') === 'first-machine';

  const visible = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    return (documents.data?.documents ?? []).filter((document) => {
      const searchMatch = !term || document.filename.toLocaleLowerCase().includes(term) || document.fileType.toLocaleLowerCase().includes(term);
      const statusMatch = removedView || filter === 'all' || document.uiStatus === filter;
      return searchMatch && statusMatch;
    });
  }, [documents.data?.documents, filter, removedView, search]);

  const pager = useIncrementalItems(visible, STANDARD_LIST_BATCH_SIZE, `${auth.activeCustomerId}:${machineId}:${removedView}:${filter}:${search}`);

  const learningCount = documents.data?.documents.filter((document) => document.uiStatus === 'learning' || document.uiStatus === 'uploading').length ?? 0;
  const reviewCount = documents.data?.documents.filter((document) => document.uiStatus === 'needs-review').length ?? 0;
  const interruptedCount = documents.data?.documents.filter((document) => document.uiStatus === 'upload-interrupted').length ?? 0;
  const activeReconciliationCount = documents.data?.documents.filter((document) => ['removing', 'removal-retrying'].includes(document.uiStatus)).length ?? 0;
  const reconciliationAttentionCount = documents.data?.documents.filter((document) => document.uiStatus === 'removal-attention').length ?? 0;
  const unfinishedReconciliationCount = activeReconciliationCount + reconciliationAttentionCount;

  useEffect(() => {
    if (!removedView) {
      previousReconcilingCount.current = null;
      setReconciliationCompleted(false);
      return;
    }
    if (previousReconcilingCount.current !== null && previousReconcilingCount.current > 0 && unfinishedReconciliationCount === 0) {
      setReconciliationCompleted(true);
    } else if (unfinishedReconciliationCount > 0) {
      setReconciliationCompleted(false);
    }
    previousReconcilingCount.current = unfinishedReconciliationCount;
  }, [unfinishedReconciliationCount, removedView]);

  const reconciliationGuidance = documentReconciliationCompletedGuidance();

  if (!auth.can(permissions.documentsRead)) return <Navigate to={`/machines/${machineId}`} replace />;

  return (
    <section className="r42-page r42-page--wide r42-documents-page">
      <header className="r42-page-header">
        <div>
          <span className="r42-eyebrow">Machine knowledge</span>
          <h1>Documents</h1>
          <p>{machine?.name ? `Information Route42 can learn from for ${machine.name}.` : 'Information Route42 can learn from for this machine.'}</p>
        </div>
        {auth.can(permissions.documentsUpload) ? (
          <Link className="btn r42-btn-primary" to={`/machines/${machineId}/documents/add`}>
            <i className="bi bi-plus-lg" aria-hidden="true" /> Add information
          </Link>
        ) : null}
      </header>

      <nav className="r42-machine-status-tabs r42-document-lifecycle-tabs" aria-label="Document lifecycle">
        <Link className={!removedView ? 'is-active' : ''} to={`/machines/${machineId}/documents`}>Active</Link>
        <Link className={removedView ? 'is-active' : ''} to={`/machines/${machineId}/documents?view=removed`}>Removed</Link>
      </nav>

      {removedView ? (
        <div className="r42-document-removed-guidance">
          <i className="bi bi-archive" aria-hidden="true" />
          <div>
            <strong>Removed sources are kept here for history.</strong>
            <span>Route42 no longer uses these sources as evidence. They remain inspectable so your team can understand what changed and why.</span>
          </div>
        </div>
      ) : null}

      {removedView && reconciliationCompleted ? (
        <div className="r42-machine-lifecycle-notice" role="status">
          <i className="bi bi-check-circle" aria-hidden="true" />
          <div><strong>{reconciliationGuidance.title}</strong><span>{reconciliationGuidance.body}</span></div>
        </div>
      ) : null}

      {removedView && reconciliationAttentionCount > 0 ? (
        <div className="r42-document-attention-banner r42-document-attention-banner--recovery" role="alert">
          <i className="bi bi-exclamation-triangle" aria-hidden="true" />
          <div>
            <strong>Machine Memory update needs attention.</strong>
            <span>{reconciliationAttentionCount === 1 ? 'A removed source is' : `${reconciliationAttentionCount} removed sources are`} still safely excluded, but Route42 stopped after bounded retries. Open a source marked Needs attention to retry the update.</span>
          </div>
        </div>
      ) : removedView && activeReconciliationCount > 0 ? (
        <div className="r42-document-attention-banner r42-document-attention-banner--reconciling" role="status">
          <i className="bi bi-arrow-repeat" aria-hidden="true" />
          <div>
            <strong>Route42 is {documents.data?.documents.some((document) => document.uiStatus === 'removal-retrying') ? 'retrying' : 'updating'} Machine Memory after {activeReconciliationCount === 1 ? 'a source was' : `${activeReconciliationCount} sources were`} removed.</strong>
            <span>{activeReconciliationCount === 1 ? 'That source is' : 'Those sources are'} already excluded from new answers. Stale Machine Memory stays fenced until the active evidence rebuild completes.</span>
          </div>
        </div>
      ) : null}

      {!removedView && learningCount > 0 ? (
        <div className="r42-learning-banner" role="status">
          <span className="r42-learning-dot" aria-hidden="true" />
          <div><strong>Route42 is learning from {learningCount} {learningCount === 1 ? 'source' : 'sources'}.</strong><span>Documents and images update automatically as processing advances.</span></div>
        </div>
      ) : null}

      {!removedView && reviewCount > 0 ? (
        <div className="r42-document-attention-banner r42-document-attention-banner--review" role="status">
          <i className="bi bi-person-check" aria-hidden="true" />
          <div><strong>{reviewCount} {reviewCount === 1 ? 'document needs' : 'documents need'} a human decision.</strong><span>Route42 paused before using {reviewCount === 1 ? 'it' : 'them'} as machine knowledge.</span></div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setFilter('needs-review')}>Review {reviewCount === 1 ? 'document' : 'documents'}</button>
        </div>
      ) : null}

      {!removedView && interruptedCount > 0 ? (
        <div className="r42-document-attention-banner r42-document-attention-banner--upload" role="status">
          <i className="bi bi-cloud-slash" aria-hidden="true" />
          <div><strong>{interruptedCount} {interruptedCount === 1 ? 'upload did' : 'uploads did'} not finish.</strong><span>Route42 never received {interruptedCount === 1 ? 'that file' : 'those files'}, so no machine knowledge was created from {interruptedCount === 1 ? 'it' : 'them'}.</span></div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setFilter('upload-interrupted')}>Resolve {interruptedCount === 1 ? 'upload' : 'uploads'}</button>
        </div>
      ) : null}

      {documents.isPending ? (
        <div className="r42-list-panel" data-testid="documents-loading" aria-label="Loading documents">{Array.from({ length: 3 }, (_, index) => <DocumentSkeleton key={index} />)}</div>
      ) : documents.isError ? (
        <div className="r42-inline-state r42-inline-state--error">
          <span>Route42 couldn't load this machine's {removedView ? 'removed sources' : 'documents'}.</span>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => void documents.refetch()}>Try again</button>
        </div>
      ) : (documents.data?.documents.length ?? 0) === 0 ? (
        removedView ? (
          <div className="r42-empty-state r42-document-empty">
            <i className="bi bi-archive" aria-hidden="true" />
            <h2>No removed sources</h2>
            <p>Sources you intentionally remove from Machine Memory will appear here for historical inspection.</p>
            <Link className="btn btn-outline-secondary" to={`/machines/${machineId}/documents`}>Back to active documents</Link>
          </div>
        ) : firstMachineOnboarding ? (
          <div className="r42-empty-state r42-document-empty r42-first-knowledge-state">
            <span className="r42-onboarding-success"><i className="bi bi-check-lg" aria-hidden="true" /> Machine created</span>
            <i className="bi bi-file-earmark-arrow-up" aria-hidden="true" />
            <h2>{machine?.name ? `${machine.name} is ready` : 'Your machine is ready'}</h2>
            <p>Now give Route42 something to learn from. Start with information your team already has about this machine.</p>
            <div className="r42-knowledge-examples" aria-label="Examples of machine knowledge">
              <span><i className="bi bi-journal-text" aria-hidden="true" /> Manuals</span>
              <span><i className="bi bi-tools" aria-hidden="true" /> Maintenance logs</span>
              <span><i className="bi bi-clipboard2-check" aria-hidden="true" /> Inspections</span>
              <span><i className="bi bi-card-text" aria-hidden="true" /> Technician notes</span>
              <span><i className="bi bi-camera" aria-hidden="true" /> Machine images</span>
            </div>
            {auth.can(permissions.documentsUpload) ? (
              <Link className="btn r42-btn-primary" to={`/machines/${machineId}/documents/add?onboarding=first-machine`}>Add your first source <i className="bi bi-arrow-right" aria-hidden="true" /></Link>
            ) : null}
            <small>Route42 begins learning as soon as the upload arrives.</small>
          </div>
        ) : (
          <div className="r42-empty-state r42-document-empty">
            <i className="bi bi-file-earmark-arrow-up" aria-hidden="true" />
            <h2>No machine information yet</h2>
            <p>Add manuals, maintenance history, inspection reports, technician notes, spreadsheets, or machine images. Route42 will begin learning as soon as each upload arrives.</p>
            {auth.can(permissions.documentsUpload) ? (
              <Link className="btn r42-btn-primary" to={`/machines/${machineId}/documents/add`}>Add information</Link>
            ) : null}
          </div>
        )
      ) : (
        <>
          <div className="r42-document-toolbar">
            <label className="r42-search" aria-label={removedView ? 'Search removed documents and images' : 'Search documents and images'}>
              <i className="bi bi-search" aria-hidden="true" />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={removedView ? 'Search removed sources' : 'Search documents and images'} />
            </label>
            {!removedView ? (
              <div className="r42-document-filters" role="group" aria-label="Filter documents">
                {activeFilters.map((item) => (
                  <button key={item.value} type="button" aria-pressed={filter === item.value} className={filter === item.value ? 'active' : ''} onClick={() => setFilter(item.value)}>{item.label}</button>
                ))}
              </div>
            ) : null}
          </div>

          {visible.length ? (
            <div className="r42-list-panel r42-document-list">
              {pager.visible.map((document) => (
                <Link className={`r42-document-row${removedView ? ' r42-document-row--removed' : ''}`} key={document.id} to={`/machines/${machineId}/documents/${document.id}`}>
                  <span className="r42-document-icon"><i className={fileIcon(document.fileType)} aria-hidden="true" /></span>
                  <span className="r42-document-body">
                    <strong>{document.filename}</strong>
                    <span>{document.stageLabel}</span>
                  </span>
                  <span className="r42-document-meta">
                    <small>{document.fileType} · {formatFileSize(document.fileSizeBytes)}</small>
                    <DocumentStatusBadge status={document.uiStatus} />
                  </span>
                  <i className="bi bi-chevron-right r42-document-chevron" aria-hidden="true" />
                </Link>
              ))}
              <IncrementalListFooter label="documents" loaded={pager.loaded} total={pager.total} hasMore={pager.hasMore} onLoadMore={pager.loadMore} />
            </div>
          ) : (
            <div className="r42-empty-state r42-document-filter-empty"><h2>No matching sources</h2><p>Try a different search{removedView ? '.' : ' or status filter.'}</p></div>
          )}
        </>
      )}
    </section>
  );
}

function DocumentSkeleton() {
  return <div className="r42-skeleton-row"><span /><div><b /><small /></div></div>;
}

function fileIcon(fileType: string): string {
  const type = fileType.toLocaleLowerCase();
  if (type === 'pdf') return 'bi bi-file-earmark-pdf';
  if (['csv', 'xlsx'].includes(type)) return 'bi bi-file-earmark-spreadsheet';
  if (['png', 'jpg', 'jpeg', 'webp'].includes(type)) return 'bi bi-file-earmark-image';
  return 'bi bi-file-earmark-text';
}
